/**
 * Classifies a raw `/guest/v1/auth/*` response into one of a small, closed set of outcomes — the
 * shell never branches on a raw HTTP status itself (DR A2, backend T13 wire shapes).
 *
 * `429` → `wait`; a `503` response OR a rejected `fetch` (network error, passed in as `res === null`)
 * → `unavailable`; a `2xx` is parsed against `expect`'s shape, and anything that doesn't match —
 * including an unparseable `2xx` body — is `refused`. **Every other status is `refused` WITHOUT
 * reading the body**: a `418`/`400`/anything-not-explicitly-handled never gets its body read, so a
 * malformed or oversized refusal body can never crash the classifier.
 */

export type GuestAnswer<T> =
  | { kind: 'ok'; data: T }
  | { kind: 'sent' }
  | { kind: 'closed'; portalPath: string }
  | { kind: 'refused' }
  | { kind: 'wait' }
  | { kind: 'unavailable' };

/** `'session'` expects a JSON session body; `'challenge'` expects an empty `202` (sent) or `200 { portalPath }` (closed). */
export type ExpectedShape = 'session' | 'challenge';

async function parseJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return undefined;
  }
}

export async function classifyGuestResponse<T>(
  res: Response | null,
  expect: ExpectedShape,
): Promise<GuestAnswer<T>> {
  if (res === null) return { kind: 'unavailable' };
  if (res.status === 429) return { kind: 'wait' };
  if (res.status === 503) return { kind: 'unavailable' };

  if (res.status < 200 || res.status >= 300) return { kind: 'refused' };

  if (expect === 'challenge') {
    if (res.status === 202) return { kind: 'sent' };
    const body = await parseJson(res);
    if (
      body !== undefined &&
      body !== null &&
      typeof body === 'object' &&
      typeof (body as Record<string, unknown>).portalPath === 'string'
    ) {
      return { kind: 'closed', portalPath: (body as { portalPath: string }).portalPath };
    }
    return { kind: 'refused' };
  }

  // expect === 'session'
  const body = await parseJson(res);
  if (
    body !== undefined &&
    body !== null &&
    typeof body === 'object' &&
    typeof (body as Record<string, unknown>).grantId === 'string' &&
    typeof (body as Record<string, unknown>).resourceKind === 'string'
  ) {
    return { kind: 'ok', data: body as T };
  }
  return { kind: 'refused' };
}

/**
 * Validates a `portalPath` as safe to `location.assign` — same-origin, single leading `/`, never
 * `//host/...` (protocol-relative) or `/\host` (browsers normalise backslashes to forward slashes,
 * so `/\evil.example` is treated as `//evil.example` by some browsers). Returns `null` if unsafe.
 */
export function safePortalPath(path: string): string | null {
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return null;
  try {
    const resolved = new URL(path, window.location.origin);
    return resolved.origin === window.location.origin ? path : null;
  } catch {
    return null;
  }
}
